import React, { useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';

// react-pintura
import { PinturaEditor } from '@pqina/react-pintura';

// pintura
import '@pqina/pintura/pintura.css';

import {
  // editor
  createDefaultImageReader,
  createDefaultImageWriter,
  createDefaultShapePreprocessor,

  // plugins
  setPlugins,
  findNode,
  createMarkupEditorToolbar,
  removeNode,
  createNode,
  getEditorDefaults
} from '@pqina/pintura';

import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import { useTranslate } from '@tolgee/react';
import { getLanguage } from '../../../../locales/i18n';
import {
  addCropObject,
  editImageThumbnail,
  saveEditorFullImage,
  updateEditorCloseState,
  updateMediaAssetFile,
  updateThumbnailProcessing
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import { Button, ProgressBar } from 'react-bootstrap';
import {
  decimalToRatio,
  getImageDimensions,
  scaleDownImage,
  getValidMimeType
} from '../../utils/helpers';
import {
  EditorImageConfig,
  generateFirebaseUploadUrl
} from '../../../../redux/slices/lomavisCreator/utils';
import axios from 'axios';
import axiosClient from '../../../../utils/axios';
import toast from 'react-hot-toast';
import { EDITOR_VIEW, UPLOAD_VIEW } from '../../config/constants';
import Carousel from './Carousel';
import { UPLOAD_TYPE_POST_MEDIA } from '../../../../config/constants';
import {
  CROP_RATIOS,
  CROP_RATIO_CUSTOM,
  POST_TYPE_KEY,
  platformFeatures
} from '../../config/platformFeatures';

setPlugins(EditorImageConfig.plugins);

const getLocale = (lang) => {
  switch (lang) {
    case 'de':
    case 'de-DE':
      return {
        ...EditorImageConfig.DeLocale
      };
    case 'en':
    case 'en-GB':
    default:
      return {
        ...EditorImageConfig.EnLocale
      };
  }
};

const editorDefaults = getEditorDefaults({
  utils: [
    'crop',
    'finetune',
    'filter',
    'annotate',
    'sticker',
    'fill',
    'frame',
    'redact'
  ]
});

const browse = (options) => {
  return new Promise((resolve, reject) => {
    const element = document.createElement('input');
    element.type = 'file';
    element.accept = options.accept;
    element.onchange = () => {
      const [file] = element.files;
      if (!file) return resolve(null);
      resolve(file);
    };
    element.click();
  });
};
const stringifyImageState = (imageState) => {
  return JSON.stringify(imageState, (k, v) => (v === undefined ? null : v));
};

export default function Editor({
  platform,
  startSubmission,
  footerRef,
  rightMargin,
  setStartSubmission,
  mediaFiles,
  handleClose,
  fileInputRef,
  successNotification,
  setSelectedIndex,
  selectedIndex
}) {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const [locale, setLocale] = useState(getLocale(getLanguage()));
  const editorRef = useRef(null);
  const [storedImageState, setStoredImageState] = useState(
    '{"cropLimitToImage": false }'
  );
  const selectedFile = mediaFiles[selectedIndex];
  const preferredMimeType = getValidMimeType(selectedFile.file_type);

  const [processingImage, setProcessingImage] = useState(false);

  const [progress, setProgress] = useState(0);

  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState(EDITOR_VIEW);
  const [currentImage, setCurrentImage] = useState('');
  const [editorUpdateCount, setEditorUpdateCount] = useState(0);
  const [editorChanged, setEditorChanged] = useState(false);

  const location_uuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.locationUuid
  );
  const cloud_uuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.cloudUuid
  );
  const postDataCropList = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData?.[platform]?.cropList
  );

  const handleInputChange = (e) => {
    // Exit if no files selected
    const fileList = e.target.files; // This is a FileList
    const fileArray = Array.from(fileList); // Convert the FileList to an array

    fileArray.map(async (file: any) => {
      let preview_image: any = URL.createObjectURL(file);

      let assetBlob: any = URL.createObjectURL(file);

      if (file) {
        try {
          const resizedBlob = await scaleDownImage(file);
          const url = URL.createObjectURL(resizedBlob);
          setUploadedImage(url);
        } catch (error) {
          console.error(error);
          setUploadedImage(preview_image);
        }
      }
    });

    fileInputRef.current.value = '';
  };

  const editor2Ref = useRef(null);
  const handleEditorInit = (editor) => {
    editorRef.current = editor;
  };

  const [addCrop, setAddCrop] = useState(false);
  useEffect(() => {
    setLocale(getLocale(getLanguage()));
  }, [getLanguage()]);

  const editorDefaults = getEditorDefaults({
    ...EditorImageConfig.ImageEditorDefaults,
    imageWriter: createDefaultImageWriter({
      mimeType: preferredMimeType,
      quality: 1.0,
      targetSize: {
        width: 1920,
        height: 1920,
        upscale: false
      }
    }),
    locale
  });

  const preview_image = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform].uploadedImageThumbnail.url
  );

  const exportImage = async () => {
    setProcessingImage(true);
    if (editorRef.current) {
      const editor = editorRef.current;
      const editorHistory =
        editorRef.current.history.get()[
          editorRef.current.history.get().length - 1
        ];

      if (
        [
          'annotation',
          'decoration',
          'manipulation',
          'redaction',
          'selection'
        ].every((key) => editorHistory?.[key]?.length === 0) &&
        !editorHistory?.colorMatrix &&
        !editorHistory?.frame &&
        editorHistory?.rotation === 0
      ) {
        setProcessingImage(false);
        setStartSubmission(true);

        setCurrentView(UPLOAD_VIEW);
        dispatch(
          addCropObject({
            platform: platform,
            cropInfo: {
              width: editorHistory?.crop?.width, // width of the selected crop
              height: editorHistory?.crop?.height, // height of the selected crop
              x: editorHistory?.crop?.x, // start x coordinate of the crop starting
              y: editorHistory?.crop?.y, // start y coordinate of the crop starting
              uuid: selectedFile?.uuid, // uuid of the image the crop belongs to
              aspect_ratio_width: decimalToRatio(editorHistory?.cropAspectRatio)
                .width, // select crop aspect ratio width
              aspect_ratio_height: decimalToRatio(
                editorHistory?.cropAspectRatio
              ).height // selected crop aspect ratio height
            }
          })
        );

        setStartSubmission(false);
        setEditorUpdateCount(0);
        dispatch(
          updateEditorCloseState({
            state: false
          })
        );
        setEditorChanged(false);
        successNotification();
        setCurrentView(EDITOR_VIEW);
        setProgress(0);
        return;
      }

      // Get the edited image

      editor.processImage().then(async (result) => {
        // Handle the resulting image data (base64 or blob)
        const editedImage = result.dest; // This will be a blob

        const url = URL.createObjectURL(editedImage);

        const dimensions = await getImageDimensions(url);
        setCurrentImage(url);
        setProcessingImage(false);
        setStartSubmission(true);
        // Do something with the URL, e.g., display it, download it, etc.

        const uploadPreset = await generateFirebaseUploadUrl(
          {
            content_type: preferredMimeType,
            filesize_in_bytes: editedImage.size,
            filename: selectedFile.name,
            uuid: selectedFile.source_media_uuid ? selectedFile.uuid : null,
            location_uuid: location_uuid ? location_uuid : null,
            cloud_uuid: cloud_uuid ? cloud_uuid : null,
            width: dimensions?.width,
            height: dimensions?.height,
            upload_type: UPLOAD_TYPE_POST_MEDIA,
            source_media_uuid: selectedFile.source_media_uuid
              ? selectedFile.source_media_uuid
              : selectedFile.uuid
          },
          {},
          () => console.log('error')
        );

        setCurrentView(UPLOAD_VIEW);

        putWithFile(
          uploadPreset?.url,
          editedImage,
          uploadPreset?.headers,
          preferredMimeType,
          uploadPreset
        )
          .then((responseData) => {
            setStartSubmission(false);
            dispatch(
              updateMediaAssetFile({
                uuid: selectedFile?.uuid,
                preview_image: url,
                file: editedImage,
                assetBlob: url,
                platform: platform,
                width: dimensions?.width,
                height: dimensions?.height,
                uploadPresets: uploadPreset,
                source_media_uuid: selectedFile.source_media_uuid
                  ? selectedFile.source_media_uuid
                  : selectedFile?.uuid
              })
            );
            setEditorUpdateCount(0);
            dispatch(
              updateEditorCloseState({
                state: false
              })
            );
            setEditorChanged(false);
            successNotification();
            setCurrentView(EDITOR_VIEW);
            setProgress(0);
          })
          .catch((error) => {
            setStartSubmission(false);
            console.error('Upload failed:', error);
            setProgress(0);
            setCurrentView(EDITOR_VIEW);
          });
      });
    }
  };

  const putWithFile = async (url, file, headers, file_type, uploadPreset) => {
    try {
      const config = {
        headers: headers ? { ...headers, 'Content-Type': file_type } : {} // Set headers property with provided headers
      };
      const response = await axios.put(url, file, {
        ...config,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        }
      });

      try {
        const res = await axiosClient.put(uploadPreset?.confirm_url, {
          success: true
        });

        return res.data;
      } catch (err: any) {
        console.log(err);
      }
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message);
      } else {
        const res = error.response;

        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.error(`Looks like there was a problem. Status Code: ${error}`);

        console.error('Error:', error);
        throw error;
      }
      toast.error(` ${t('errors.request_couldnt_be_completed')}`, {
        duration: 4000,
        position: 'top-right'
      });

      // Dispatch deleteAfile action inside the catch block
    }
  };

  const willRenderToolbar = (toolbar) => {
    // get the export button
    const exportButton = findNode('export', toolbar);

    removeNode('export', toolbar);

    return toolbar;
  };
  ///////////////////////////////

  const handleEditorUpdate = async (imageState) => {
    setEditorUpdateCount((prevState) => prevState + 1);

    const imageStateStr = stringifyImageState(imageState);

    setStoredImageState(imageStateStr);

    if (editorRef.current?.history) {
      const prevEditorHistory =
        editorRef.current?.history?.get()[
          editorRef.current?.history?.get().length - 1
        ];

      if (imageState?.cropAspectRatio !== prevEditorHistory?.cropAspectRatio) {
        if (imageState?.cropAspectRatio === undefined) {
          handleCropChange(imageState);
        }
      }
    }
  };

  const selectAnImage = async () => {
    const annotations = editorRef.current.editor.imageAnnotation;
    const shape = annotations.find((shape) => shape.id === '');

    // browse for an image
    const file: any = await browse({
      accept: 'image/*'
    });

    // no file selected
    if (!file) return;

    // update background image
    shape.backgroundImage = URL.createObjectURL(file);

    // redraw annotations
    editorRef.current.editor.imageAnnotation = annotations;
  };
  const handleEditorProcess = (res) => {
    // 1. Turn imageState into string, replaces undefined values with null
    const imageStateStr = stringifyImageState(res.imageState);
    setStoredImageState(imageStateStr);
  };
  const handleImageUpdate = async () => {
    if (!uploadedImage || !storedImageState) {
      return;
    }

    function removeObjectsByKeys(object, keysToRemove) {
      return Object.keys(object).reduce((result, key) => {
        if (!keysToRemove.includes(key)) {
          result[key] = object[key];
        }
        return result;
      }, {});
    }
    const dimensions = await getImageDimensions(uploadedImage);

    const currentState = JSON.parse(storedImageState);
    const justAnnotations = [...currentState?.annotation];

    const filteredState = removeObjectsByKeys(currentState, ['annotation']);
    const newAnnote = {
      aspectRatio: undefined,
      backgroundColor: [0, 0, 0, 0],
      backgroundImage: uploadedImage,
      backgroundSize: 'contain',
      cornerRadius: 0,
      disableErase: true,
      disableStyle: [
        'cornerRadius',
        'backgroundColor',
        'strokeColor',
        'strokeWidth'
      ],
      height: dimensions?.height,
      id: uploadedImage,
      isEditing: false,
      isSelected: true,
      opacity: 1,
      rotation: 0,
      strokeColor: [0, 0, 0],
      strokeWidth: 0,
      width: dimensions?.width,
      x: 0,
      y: 0
    };
    const combined = {
      ...filteredState,
      annotation: [...justAnnotations, newAnnote]
    };

    const imageStateStr = stringifyImageState(combined);
    editorRef.current.history.write(JSON.parse(imageStateStr));
    editorRef.current.util = undefined;
    editorRef.current.util = 'sticker';
  };

  const handleCropChange = async (imageState) => {
    if (!editorRef.current) return;

    const src = mediaFiles[selectedIndex].assetBlob
      ? mediaFiles[selectedIndex].assetBlob
      : mediaFiles[selectedIndex].original_asset;
    const ImageDimensions = await getImageDimensions(src);

    // If "Custom" is selected, set the crop to the maximum available crop
    if (imageState?.crop) {
      editorRef.current?.history?.write({
        crop: {
          x: 0,
          y: 0,
          width: ImageDimensions.width,
          height: ImageDimensions.height
        }
      });
    }
  };

  const handleCropUpdate = () => {
    if (!storedImageState || !editorRef?.current?.history) {
      return;
    }

    const fileCropObj = postDataCropList.find(
      (obj) => obj?.uuid === selectedFile?.uuid
    );
    if (!fileCropObj) {
      return;
    }

    function removeObjectsByKeys(object, keysToRemove) {
      return Object.keys(object).reduce((result, key) => {
        if (!keysToRemove.includes(key)) {
          result[key] = object[key];
        }
        return result;
      }, {});
    }

    const currentState = JSON.parse(storedImageState);

    const filteredState = removeObjectsByKeys(currentState, [
      'crop',
      'cropAspectRatio'
    ]);

    const combined = {
      ...filteredState,
      crop: {
        height: fileCropObj?.height,
        width: fileCropObj?.width,
        x: fileCropObj?.x,
        y: fileCropObj?.y
      },
      cropAspectRatio:
        fileCropObj?.aspect_ratio_width / fileCropObj?.aspect_ratio_height
    };

    const imageStateStr = stringifyImageState(combined);
    editorRef.current.history.write(combined);
  };

  const triggerUtilityPlugin = () => {
    const editor = editorRef.current;
    editor.util = 'sticker';
  };

  useEffect(() => {
    handleImageUpdate();
  }, [uploadedImage]);
  useEffect(() => {
    setEditorUpdateCount(0);
  }, [selectedFile]);

  const typeKey = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.postTypeSelected
  );

  const postTypeRule = (platform) => {
    return platformFeatures[platform][POST_TYPE_KEY]?.find(
      (pt) => pt.typeKey === typeKey[platform]
    );
  };

  useEffect(() => {
    if (editorRef !== null) {
      handleCropUpdate();
    }
  }, [platform]);

  return (
    <div className="d-flex w-100 h-100 flex-column gap-4 ">
      <div className="d-flex w-100 gap-4 flex-grow-1 overflow-hidden">
        <div
          style={{ width: '140px' }}
          className="bg-white p-2 rounded-3 mx-auto d-flex flex-column h-100"
        >
          <Carousel
            items={mediaFiles}
            setSelectedIndex={setSelectedIndex}
            selectedFile={selectedFile}
            setEditorChanged={setEditorChanged}
            editorChanged={editorChanged}
          />
        </div>

        {selectedFile && (
          <div
            // style={{ minHeight: "500px" }}
            className="flex-grow-1 overflow-auto"
          >
            <div className=" w-100 h-100 position-relative d-flex flex-column gap-4 ">
              {processingImage && (
                <div
                  style={{ zIndex: 10, top: '0px', left: 'Opx' }}
                  className="position-absolute w-100 h-100 bg-white bg-opacity-10% rounded-3"
                ></div>
              )}
              {!editorChanged && (
                <div
                  onClick={() => {
                    dispatch(
                      updateEditorCloseState({
                        state: true
                      })
                    );
                    setEditorChanged(true);
                  }}
                  style={{ zIndex: 11, top: '0px', left: 'Opx' }}
                  className="position-absolute w-100 h-100 bg-opacity-0% rounded-3"
                ></div>
              )}
              {currentView === EDITOR_VIEW && (
                <div className="w-100 bg-white p-3 h-100 rounded-3">
                  <PinturaEditor
                    {...editorDefaults}
                    ref={editorRef}
                    stickers={[
                      ['Emoji', [...EditorImageConfig?.Stickers]],
                      ['Markers', []]
                    ]}
                    cropSelectPresetOptions={[
                      [
                        'Crop',

                        postTypeRule(
                          platform
                        ).mediaTypes?.image?.cropSuggestions?.map((item) => {
                          if (item === '0:0' || !item) {
                            return [
                              undefined,
                              t('lomavisCreator.editorCropRatio.Custom')
                            ];
                          } else {
                            return [
                              CROP_RATIOS[item]?.width /
                                CROP_RATIOS[item]?.height,
                              `${CROP_RATIOS[item]?.width}:${CROP_RATIOS[item]?.height}`
                            ];
                          }
                        })
                      ]
                    ]}
                    src={
                      mediaFiles[selectedIndex].assetBlob
                        ? mediaFiles[selectedIndex].assetBlob
                        : mediaFiles[selectedIndex].original_asset
                    }
                    onInit={handleEditorInit}
                    enableZoom={false}
                    enableZoomControls={false}
                    onReady={handleCropUpdate}
                    cropInteractionFocus={'selection'}
                    willRenderToolbar={willRenderToolbar}
                    stickerStickToImage={true}
                    onUpdate={handleEditorUpdate}
                    onProcess={handleEditorProcess}
                  ></PinturaEditor>
                </div>
              )}

              {currentView === UPLOAD_VIEW && (
                <div className="w-100 bg-white p-3 h-100 rounded-3">
                  <div
                    style={{
                      width: '80%',
                      height: '90%'
                    }}
                    className="mx-auto align-items-center justify-content-center d-flex flex-column gap-4"
                  >
                    <div className="w-100 h-100 position-relative">
                      <img
                        style={{
                          maxHeight: '100%',
                          maxWidth: '100%'
                        }}
                        className="position-absolute top-50 start-50 translate-middle object-fit-contain"
                        src={currentImage}
                        alt="Image preview"
                      />
                    </div>
                    <div style={{ height: '14px' }} className="w-50">
                      {progress > 0 && (
                        <ProgressBar
                          className="h-100"
                          variant="primary"
                          animated
                          now={progress}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        ref={footerRef}
        style={{ bottom: '0', zIndex: 100, height: '60px' }}
        className="w-100 d-flex justify-content-between gap-3 rounded-3 align-items-center bg-white py-2 px-3 border-top h-60px bg-white"
      >
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            max={1}
            style={{ display: 'none' }}
          />
        </div>
        <div
          style={{ marginRight: rightMargin + 'px' }}
          className="d-flex justify-content-end gap-3 align-items-center py-2 flax-fill"
        >
          {!startSubmission && !processingImage && (
            <Button
              onClick={() => {
                currentView === UPLOAD_VIEW
                  ? setCurrentView(EDITOR_VIEW)
                  : handleClose();
              }}
              variant="secondary"
              size="sm"
              disabled={startSubmission || processingImage}
            >
              {t('lomavisCreator.back')}
            </Button>
          )}

          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              exportImage();
            }}
            disabled={startSubmission || processingImage}
          >
            {(startSubmission || processingImage) && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
            )}
            {startSubmission || processingImage
              ? t('lomavisCreator.buttons.saving')
              : t('lomavisCreator.buttons.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
